import React, { useState, useEffect, useRef } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { useToast } from "../hooks/use-toast";
import {
  whatsappAPI,
  categoriesAPI,
  autoResponsesAPI,
  configAPI,
  welcomeQuestionsAPI,
  knowledgeAPI,
} from "../services/api";
import {
  MessageSquare,
  Plus,
  Trash2,
  Upload,
  Save,
  Settings,
  Clock,
  Calendar,
  Loader2,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";

const Config = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [whatsappStatus, setWhatsappStatus] = useState({
    connected: false,
    qr_code: null,
  });

  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState({ name: "", description: "" });

  const [autoResponses, setAutoResponses] = useState([]);
  const [newResponse, setNewResponse] = useState({
    trigger: "",
    response: "",
    active: true,
  });

  const [welcomeQuestions, setWelcomeQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState("");
  const activeMenuQuestions = welcomeQuestions.filter((question) => question.active);
  const menuPreviewOptions = (activeMenuQuestions.length ? activeMenuQuestions : autoResponses.filter((response) => response.active))
    .map((option, index) => ({
      id: option.id,
      number: index < 3 ? index + 1 : index + 2,
      title: option.question || option.trigger,
    }));
  menuPreviewOptions.push({ id: "procedure-catalog", number: 4, title: "Orientações e vídeos" });
  menuPreviewOptions.sort((first, second) => first.number - second.number);

  const [config, setConfig] = useState({
    company_name: "",
    primary_color: "#3b82f6",
    use_external_api: false,
    external_api_url: "",
    auto_menu_enabled: true,
    ai_auto_reply_enabled: false,
    welcome_message:
      "Olá, tudo bem? Seja bem-vindo ao Sistema de Atendimentos Eletrônico da WSTech Soluções em Informática. É um prazer atendê-lo(a).",
    out_of_hours_message:
      "Olá! Nosso horário de atendimento é de segunda a sexta das 08h às 17:30h e aos sábados de 08h às 12h. Deixe sua mensagem e retornaremos assim que possível!",
  });

  const [businessHours, setBusinessHours] = useState([
    {
      day: 1,
      label: "Segunda-feira",
      active: true,
      start: "08:00",
      end: "17:30",
    },
    {
      day: 2,
      label: "Terça-feira",
      active: true,
      start: "08:00",
      end: "17:30",
    },
    {
      day: 3,
      label: "Quarta-feira",
      active: true,
      start: "08:00",
      end: "17:30",
    },
    {
      day: 4,
      label: "Quinta-feira",
      active: true,
      start: "08:00",
      end: "17:30",
    },
    {
      day: 5,
      label: "Sexta-feira",
      active: true,
      start: "08:00",
      end: "17:30",
    },
    { day: 6, label: "Sábado", active: true, start: "08:00", end: "12:00" },
    { day: 7, label: "Domingo", active: false, start: "08:00", end: "12:00" },
  ]);
  const [holidays, setHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState({ date: "", description: "" });

  const [tutorials, setTutorials] = useState([]);
  const [newTutorial, setNewTutorial] = useState({
    title: "",
    symptoms: "",
    solution_text: "",
    media_url: "",
    active: true,
  });
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const mediaInputRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    let intervalId;

    if (polling && !whatsappStatus.connected) {
      intervalId = setInterval(async () => {
        try {
          const response = await whatsappAPI.getStatus();
          setWhatsappStatus(response.data);

          if (response.data.connected) {
            setPolling(false);
            toast({
              title: "WhatsApp Conectado!",
              description: "Conexão estabelecida com sucesso.",
            });
          }
        } catch (error) {
          console.error("Error polling status:", error);
        }
      }, 2000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [polling, whatsappStatus.connected, toast]);

  const loadData = async () => {
    try {
      const [whatsapp, cats, responses, conf, questions, hoursData, tuts] =
        await Promise.all([
          whatsappAPI.getStatus(),
          categoriesAPI.getAll(),
          autoResponsesAPI.getAll(),
          configAPI.get(),
          welcomeQuestionsAPI.getAll(),
          configAPI.getHours(),
          knowledgeAPI.getAll(),
        ]);

      setWhatsappStatus(whatsapp.data);
      setCategories(cats.data);
      setAutoResponses(responses.data);
      setConfig(conf.data);
      setWelcomeQuestions(questions.data);
      setTutorials(tuts.data);

      if (
        hoursData.data.business_hours &&
        hoursData.data.business_hours.length > 0
      ) {
        setBusinessHours(hoursData.data.business_hours);
      }
      if (hoursData.data.holidays) {
        setHolidays(hoursData.data.holidays);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const connectWhatsApp = async () => {
    setLoading(true);
    try {
      const response = await whatsappAPI.connect();

      setTimeout(async () => {
        try {
          const statusResponse = await whatsappAPI.getStatus();
          setWhatsappStatus(statusResponse.data);

          if (statusResponse.data.qr_code) {
            setPolling(true);
            toast({
              title: "QR Code gerado!",
              description:
                "Escaneie o QR Code com seu WhatsApp. A página atualizará automaticamente.",
            });
          }
        } catch (error) {
          console.error("Error fetching status:", error);
        }
      }, 2500);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao conectar WhatsApp.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const disconnectWhatsApp = async () => {
    try {
      setPolling(false);
      await whatsappAPI.disconnect();
      setWhatsappStatus({ connected: false, qr_code: null });
      toast({
        title: "WhatsApp desconectado",
        description: "Conexão encerrada com sucesso.",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao desconectar.",
        variant: "destructive",
      });
    }
  };

  const addCategory = async () => {
    if (!newCategory.name) return;

    try {
      const response = await categoriesAPI.create(newCategory);
      setCategories([...categories, response.data]);
      setNewCategory({ name: "", description: "" });
      toast({
        title: "Categoria adicionada",
        description: "Nova categoria criada com sucesso.",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao adicionar categoria.",
        variant: "destructive",
      });
    }
  };

  const deleteCategory = async (id) => {
    try {
      await categoriesAPI.delete(id);
      setCategories(categories.filter((c) => c.id !== id));
      toast({
        title: "Categoria removida",
        description: "Categoria excluída com sucesso.",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao remover categoria.",
        variant: "destructive",
      });
    }
  };

  const addAutoResponse = async () => {
    if (!newResponse.trigger || !newResponse.response) return;

    try {
      const response = await autoResponsesAPI.create(newResponse);
      setAutoResponses([...autoResponses, response.data]);
      setNewResponse({ trigger: "", response: "", active: true });
      toast({
        title: "Resposta automática adicionada",
        description: "Nova resposta criada com sucesso.",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao adicionar resposta.",
        variant: "destructive",
      });
    }
  };

  const toggleAutoResponse = async (id, active) => {
    try {
      await autoResponsesAPI.update(id, { active });
      setAutoResponses(
        autoResponses.map((r) => (r.id === id ? { ...r, active } : r)),
      );
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao atualizar resposta.",
        variant: "destructive",
      });
    }
  };

  const deleteAutoResponse = async (id) => {
    try {
      await autoResponsesAPI.delete(id);
      setAutoResponses(autoResponses.filter((r) => r.id !== id));
      toast({
        title: "Resposta removida",
        description: "Resposta automática excluída.",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao remover resposta.",
        variant: "destructive",
      });
    }
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingMedia(true);
    try {
      const response = await knowledgeAPI.uploadMedia(file);
      setNewTutorial({ ...newTutorial, media_url: response.data.url });
      toast({
        title: "Upload Concluído",
        description: "Arquivo anexado ao tutorial com sucesso!",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Falha ao enviar arquivo para o servidor.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingMedia(false);
      if (mediaInputRef.current) mediaInputRef.current.value = "";
    }
  };

  const addTutorial = async () => {
    if (!newTutorial.title || !newTutorial.solution_text) return;
    try {
      const response = await knowledgeAPI.create(newTutorial);
      setTutorials([response.data, ...tutorials]);
      setNewTutorial({
        title: "",
        symptoms: "",
        solution_text: "",
        media_url: "",
        active: true,
      });
      toast({
        title: "Tutorial adicionado",
        description: "A IA já pode usar essa solução!",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao salvar tutorial.",
        variant: "destructive",
      });
    }
  };

  const deleteTutorial = async (id) => {
    try {
      await knowledgeAPI.delete(id);
      setTutorials(tutorials.filter((t) => t.id !== id));
      toast({ title: "Removido", description: "Tutorial apagado." });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao apagar.",
        variant: "destructive",
      });
    }
  };

  const addWelcomeQuestion = async () => {
    if (!newQuestion.trim()) return;

    try {
      const response = await welcomeQuestionsAPI.create({
        question: newQuestion,
        order: welcomeQuestions.length,
        active: true,
      });
      setWelcomeQuestions([...welcomeQuestions, response.data]);
      setNewQuestion("");
      toast({
        title: "Pergunta adicionada",
        description: "Nova opção de menu criada.",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao adicionar pergunta.",
        variant: "destructive",
      });
    }
  };

  const deleteWelcomeQuestion = async (id) => {
    try {
      await welcomeQuestionsAPI.delete(id);
      setWelcomeQuestions(welcomeQuestions.filter((q) => q.id !== id));
      toast({
        title: "Pergunta removida",
        description: "Opção de menu excluída.",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao remover pergunta.",
        variant: "destructive",
      });
    }
  };

  const toggleWelcomeQuestion = async (id, active) => {
    try {
      await welcomeQuestionsAPI.update(id, { active });
      setWelcomeQuestions(
        welcomeQuestions.map((q) => (q.id === id ? { ...q, active } : q)),
      );
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao atualizar pergunta.",
        variant: "destructive",
      });
    }
  };

  const saveConfig = async () => {
    try {
      await configAPI.update(config);
      await configAPI.updateHours({
        business_hours: businessHours,
        holidays: holidays,
      });

      toast({
        title: "Configurações salvas",
        description: "Alterações salvas com sucesso.",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao salvar configurações.",
        variant: "destructive",
      });
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const response = await configAPI.uploadLogo(file);
      toast({
        title: "Logo atualizado",
        description: "Logo enviado com sucesso.",
      });
      loadData();
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao enviar logo.",
        variant: "destructive",
      });
    }
  };

  return (
    <div data-testid="config-page">
      <h1 className="text-2xl sm:text-3xl font-bold mb-6 flex items-center gap-2 text-gray-800">
        <Settings className="w-8 h-8 text-gray-600 shrink-0" />
        Configurações
      </h1>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <Tabs defaultValue="whatsapp">
          <div className="w-full overflow-x-auto pb-2 -mb-2 no-scrollbar">
            <TabsList className="flex w-max min-w-full justify-start md:justify-between p-1 bg-gray-100/80 rounded-lg">
              <TabsTrigger
                value="whatsapp"
                data-testid="tab-whatsapp"
                className="flex-1 whitespace-nowrap"
              >
                WhatsApp
              </TabsTrigger>
              <TabsTrigger
                value="welcome-menu"
                data-testid="tab-welcome-menu"
                className="flex-1 whitespace-nowrap"
              >
                Menu Boas-Vindas
              </TabsTrigger>
              <TabsTrigger
                value="categories"
                data-testid="tab-categories"
                className="flex-1 whitespace-nowrap"
              >
                Categorias
              </TabsTrigger>
              <TabsTrigger
                value="hours"
                data-testid="tab-hours"
                className="flex-1 whitespace-nowrap"
              >
                Horários
              </TabsTrigger>
              <TabsTrigger
                value="auto-responses"
                data-testid="tab-auto-responses"
                className="flex-1 whitespace-nowrap"
              >
                Respostas Auto
              </TabsTrigger>
              <TabsTrigger
                value="tutorials"
                className="flex-1 whitespace-nowrap"
              >
                Base da IA
              </TabsTrigger>
              <TabsTrigger
                value="api-externa"
                data-testid="tab-api-externa"
                className="flex-1 whitespace-nowrap"
              >
                API Externa
              </TabsTrigger>
              <TabsTrigger
                value="general"
                data-testid="tab-general"
                className="flex-1 whitespace-nowrap"
              >
                Geral
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="whatsapp">
            <Card>
              <CardHeader>
                <CardTitle>Conexão WhatsApp Web</CardTitle>
                <CardDescription>
                  Conecte sua conta do WhatsApp para receber e enviar mensagens
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-0 items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <MessageSquare className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="font-medium">Status da Conexão</p>
                      <p
                        className={`text-sm ${whatsappStatus.connected ? "text-green-600" : "text-gray-500"}`}
                      >
                        {whatsappStatus.connected
                          ? "Conectado"
                          : "Desconectado"}
                      </p>
                    </div>
                  </div>
                  {whatsappStatus.connected ? (
                    <Button
                      variant="destructive"
                      onClick={disconnectWhatsApp}
                      data-testid="disconnect-whatsapp"
                    >
                      Desconectar
                    </Button>
                  ) : (
                    <Button
                      onClick={connectWhatsApp}
                      disabled={loading}
                      data-testid="connect-whatsapp"
                    >
                      {loading ? "Conectando..." : "Conectar"}
                    </Button>
                  )}
                </div>

                {whatsappStatus.qr_code && !whatsappStatus.connected && (
                  <div className="text-center p-6 bg-white border-2 border-dashed rounded-lg">
                    <img
                      src={whatsappStatus.qr_code}
                      alt="QR Code WhatsApp"
                      className="w-64 h-64 mx-auto"
                    />
                    <p className="mt-4 text-sm text-gray-600">
                      Abra o WhatsApp no celular → Menu → Dispositivos
                      conectados → Conectar dispositivo
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Escaneie o QR Code acima para conectar
                    </p>
                    {polling && (
                      <div className="mt-4 flex items-center justify-center gap-2 text-blue-600">
                        <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                        <span className="text-sm">Aguardando conexão...</span>
                      </div>
                    )}
                    <Button
                      variant="outline"
                      onClick={connectWhatsApp}
                      disabled={loading}
                      className="mt-4"
                      data-testid="regenerate-qr"
                    >
                      {loading ? "Gerando..." : "Gerar Novo QR Code"}
                    </Button>
                  </div>
                )}

                {whatsappStatus.connected && (
                  <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-center gap-3 text-green-700">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="font-medium text-lg">
                        WhatsApp Conectado com Sucesso!
                      </span>
                    </div>
                    <p className="text-center text-green-600 text-sm mt-2">
                      Seu sistema está pronto para receber e enviar mensagens.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="welcome-menu">
            <Card>
              <CardHeader>
                <CardTitle>Menu de Boas-Vindas</CardTitle>
                <CardDescription>
                  Configure a mensagem e opções enviadas automaticamente para
                  novos clientes
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col space-y-6 w-full">
                <div className="space-y-2 w-full">
                  <Label>Mensagem de Boas-Vindas</Label>
                  <Textarea
                    placeholder="Olá, tudo bem? Seja bem-vindo ao Sistema de Atendimentos Eletrônico da WSTech Soluções em Informática. É um prazer atendê-lo(a)."
                    value={config.welcome_message || ""}
                    onChange={(e) =>
                      setConfig({ ...config, welcome_message: e.target.value })
                    }
                    rows={3}
                    data-testid="welcome-message-input"
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500">
                    Esta mensagem será enviada junto com as opções do menu
                  </p>
                </div>

                {/* Add New Question */}
                <div className="space-y-4 p-4 bg-blue-50 rounded-lg w-full">
                  <h3 className="font-medium">Adicionar Opção do Menu</h3>
                  <div className="flex flex-col sm:flex-row gap-2 w-full">
                    <Input
                      placeholder="Ex: Suporte Técnico"
                      value={newQuestion}
                      onChange={(e) => setNewQuestion(e.target.value)}
                      data-testid="welcome-question-input"
                      className="flex-1 bg-white"
                    />
                    <Button
                      onClick={addWelcomeQuestion}
                      data-testid="add-question-btn"
                      className="w-full sm:w-auto"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Adicionar
                    </Button>
                  </div>
                </div>

                <div className="space-y-2 w-full">
                  <Label>Opções do Menu</Label>
                  <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
                    <strong>4. Orientações e vídeos</strong>
                    <p className="mt-1">Opção automática. Na biblioteca de procedimentos, marque “Disponibilizar no bot de orientações” nos vídeos que os clientes poderão consultar.</p>
                  </div>
                  {welcomeQuestions.map((question, index) => (
                    <div
                      key={question.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg w-full"
                      data-testid={`question-item-${question.id}`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0 pr-4">
                        <span className="w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-800 rounded-full font-medium shrink-0">
                          {index < 3 ? index + 1 : index + 2}
                        </span>
                        <span
                          className={`truncate ${
                            question.active ? "" : "text-gray-400 line-through"
                          }`}
                        >
                          {question.question}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Switch
                          checked={question.active}
                          onCheckedChange={(checked) =>
                            toggleWelcomeQuestion(question.id, checked)
                          }
                          data-testid={`toggle-question-${question.id}`}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteWelcomeQuestion(question.id)}
                          data-testid={`delete-question-${question.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {welcomeQuestions.length === 0 && (
                    <p className="text-center text-gray-500 py-8">
                      Nenhuma opção de menu configurada. As respostas
                      automáticas serão usadas como opções.
                    </p>
                  )}
                </div>

                <div className="p-4 bg-green-50 rounded-lg w-full">
                  <h3 className="font-medium text-green-800 mb-3">
                    Pré-visualização do Menu
                  </h3>
                  <div className="bg-white p-4 rounded-lg shadow-sm text-sm w-full">
                    <p className="mb-2">
                      {config.welcome_message ||
                        "Olá, tudo bem? Seja bem-vindo ao Sistema de Atendimentos Eletrônico da WSTech Soluções em Informática. É um prazer atendê-lo(a)."}
                    </p>
                    <p className="mb-2">📋 *Protocolo:* TK-XXXXXXXX</p>
                    <p className="font-medium mb-2">*Selecione uma opção:*</p>
                    {menuPreviewOptions.map((option) => (
                      <p key={option.id}>{option.number}. {option.title}</p>
                    ))}
                    <p className="mt-2 italic text-gray-500">
                      _Para agilizar o seu atendimento responda com a opção
                      desejada._
                    </p>
                  </div>
                </div>

                <div className="flex justify-end w-full pt-4">
                  <Button
                    onClick={saveConfig}
                    className="w-full sm:w-auto px-8"
                    data-testid="save-welcome-config-btn"
                  >
                    <Save className="w-4 h-4 mr-2 shrink-0" />
                    Salvar Configurações
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="categories">
            <Card>
              <CardHeader>
                <CardTitle>Categorias / Produtos</CardTitle>
                <CardDescription>
                  Gerencie as categorias de atendimento
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-2 lg:gap-4">
                    <div>
                      <Label>Nome da Categoria</Label>
                      <Input
                        placeholder="Ex: Suporte Técnico"
                        value={newCategory.name}
                        onChange={(e) =>
                          setNewCategory({
                            ...newCategory,
                            name: e.target.value,
                          })
                        }
                        data-testid="category-name-input"
                      />
                    </div>
                    <div>
                      <Label>Descrição</Label>
                      <Input
                        placeholder="Descrição opcional"
                        value={newCategory.description}
                        onChange={(e) =>
                          setNewCategory({
                            ...newCategory,
                            description: e.target.value,
                          })
                        }
                        data-testid="category-description-input"
                      />
                    </div>
                    <Button
                      onClick={addCategory}
                      data-testid="add-category-btn"
                      className="sm:mt-6"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Adicionar Categoria
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  {categories.map((category) => (
                    <div
                      key={category.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                      data-testid={`category-item-${category.id}`}
                    >
                      <div>
                        <p className="font-medium">{category.name}</p>
                        {category.description && (
                          <p className="text-sm text-gray-600">
                            {category.description}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteCategory(category.id)}
                        data-testid={`delete-category-${category.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  ))}
                  {categories.length === 0 && (
                    <p className="text-center text-gray-500 py-8">
                      Nenhuma categoria cadastrada
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="hours" className="mt-6 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  Horários de Atendimento
                </CardTitle>
                <CardDescription>
                  Configure os dias e horários em que o robô irá transferir os
                  clientes para a fila. Fora destes horários, a mensagem de
                  ausência será enviada.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2 bg-gray-50 p-4 rounded-lg border">
                  <Label className="font-semibold text-gray-700">
                    Mensagem de Fora de Expediente
                  </Label>
                  <Textarea
                    rows={3}
                    placeholder="Ex: Olá! No momento estamos fechados..."
                    value={config.out_of_hours_message || ""}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        out_of_hours_message: e.target.value,
                      })
                    }
                  />
                  <p className="text-xs text-gray-500">
                    Esta mensagem será enviada se o cliente mandar mensagem fora
                    do horário ou em um feriado.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-700 mb-3">
                    Dias da Semana
                  </h3>
                  {businessHours.map((day, index) => (
                    <div
                      key={day.day}
                      className="flex flex-col md:flex-row items-start md:items-center gap-4 p-4 bg-white border rounded-lg shadow-sm hover:border-blue-100 transition-colors"
                    >
                      <div className="w-full md:w-48 flex items-center justify-between md:justify-start gap-3">
                        <Switch
                          checked={day.active}
                          onCheckedChange={(checked) => {
                            const newHours = [...businessHours];
                            newHours[index].active = checked;
                            setBusinessHours(newHours);
                          }}
                        />
                        <span
                          className={`font-medium ${day.active ? "text-gray-900" : "text-gray-400"}`}
                        >
                          {day.label}
                        </span>
                      </div>

                      <div
                        className={`flex flex-col gap-3 w-full transition-opacity ${day.active ? "opacity-100" : "opacity-50 pointer-events-none"}`}
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider w-16">
                            Turno 1
                          </span>
                          <Input
                            type="time"
                            value={day.start}
                            onChange={(e) => {
                              const newHours = [...businessHours];
                              newHours[index].start = e.target.value;
                              setBusinessHours(newHours);
                            }}
                            className="w-28 sm:w-32"
                            disabled={!day.active}
                          />
                          <span className="text-gray-500 text-sm px-1">
                            até
                          </span>
                          <Input
                            type="time"
                            value={day.end}
                            onChange={(e) => {
                              const newHours = [...businessHours];
                              newHours[index].end = e.target.value;
                              setBusinessHours(newHours);
                            }}
                            className="w-28 sm:w-32"
                            disabled={!day.active}
                          />

                          {!day.split_shift && (
                            <button
                              onClick={() => {
                                const newHours = [...businessHours];
                                newHours[index].split_shift = true;
                                newHours[index].start2 = "13:00";
                                newHours[index].end2 = "18:00";
                                setBusinessHours(newHours);
                              }}
                              className="ml-2 text-xs text-blue-600 hover:text-blue-800 font-semibold bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-full transition-colors"
                            >
                              + Adicionar Pausa
                            </button>
                          )}
                        </div>

                        {day.split_shift && (
                          <div className="flex flex-wrap items-center gap-2 animate-in slide-in-from-top-2 duration-200">
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider w-16">
                              Turno 2
                            </span>
                            <Input
                              type="time"
                              value={day.start2 || "13:00"}
                              onChange={(e) => {
                                const newHours = [...businessHours];
                                newHours[index].start2 = e.target.value;
                                setBusinessHours(newHours);
                              }}
                              className="w-28 sm:w-32 bg-blue-50/30"
                              disabled={!day.active}
                            />
                            <span className="text-gray-500 text-sm px-1">
                              até
                            </span>
                            <Input
                              type="time"
                              value={day.end2 || "18:00"}
                              onChange={(e) => {
                                const newHours = [...businessHours];
                                newHours[index].end2 = e.target.value;
                                setBusinessHours(newHours);
                              }}
                              className="w-28 sm:w-32 bg-blue-50/30"
                              disabled={!day.active}
                            />

                            <button
                              onClick={() => {
                                const newHours = [...businessHours];
                                newHours[index].split_shift = false;
                                setBusinessHours(newHours);
                              }}
                              className="ml-2 text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 p-2 rounded-full transition-colors"
                              title="Remover segundo turno"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <Separator />

                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-orange-500" />
                    <h3 className="font-semibold text-gray-700">
                      Feriados e Datas Fechadas
                    </h3>
                  </div>
                  <p className="text-sm text-gray-500">
                    Adicione datas específicas em que o estabelecimento estará
                    fechado o dia todo.
                  </p>

                  <div className="flex flex-col sm:flex-row items-end gap-3">
                    <div className="space-y-1">
                      <Label>Data</Label>
                      <Input
                        type="date"
                        value={newHoliday.date}
                        onChange={(e) =>
                          setNewHoliday({ ...newHoliday, date: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-1 flex-1">
                      <Label>Descrição (Ex: Natal)</Label>
                      <Input
                        type="text"
                        placeholder="Descrição da data"
                        value={newHoliday.description}
                        onChange={(e) =>
                          setNewHoliday({
                            ...newHoliday,
                            description: e.target.value,
                          })
                        }
                      />
                    </div>
                    <Button
                      onClick={() => {
                        if (newHoliday.date && newHoliday.description) {
                          setHolidays([...holidays, newHoliday]);
                          setNewHoliday({ date: "", description: "" });
                        }
                      }}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4 mr-2" /> Adicionar
                    </Button>
                  </div>

                  <div className="mt-4 space-y-2">
                    {holidays.map((holiday, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-orange-50/50 border border-orange-100 rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <Badge variant="outline" className="bg-white">
                            {holiday.date.split("-").reverse().join("/")}
                          </Badge>
                          <span className="text-gray-700">
                            {holiday.description}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setHolidays(holidays.filter((_, i) => i !== idx))
                          }
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    {holidays.length === 0 && (
                      <p className="text-sm text-center text-gray-400 py-4">
                        Nenhuma data especial cadastrada.
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <Button
                    onClick={() => {
                      saveConfig();
                    }}
                    className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Salvar Horários
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="auto-responses">
            <Card>
              <CardHeader>
                <CardTitle>Respostas Automáticas</CardTitle>
                <CardDescription>
                  Configure mensagens automáticas para palavras-chave
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-2 lg:gap-4">
                    <div>
                      <Label>Palavra-chave (gatilho)</Label>
                      <Input
                        placeholder="Ex: horário, preço, contato"
                        value={newResponse.trigger}
                        onChange={(e) =>
                          setNewResponse({
                            ...newResponse,
                            trigger: e.target.value,
                          })
                        }
                        data-testid="response-trigger-input"
                      />
                    </div>
                    <div>
                      <Label>Resposta Automática</Label>
                      <Input
                        placeholder="Digite a mensagem que será enviada automaticamente"
                        value={newResponse.response}
                        onChange={(e) =>
                          setNewResponse({
                            ...newResponse,
                            response: e.target.value,
                          })
                        }
                        data-testid="response-message-input"
                      />
                    </div>
                    <Button
                      onClick={addAutoResponse}
                      className="sm:mt-6"
                      data-testid="add-response-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Adicionar Resposta
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  {autoResponses.map((response) => (
                    <div
                      key={response.id}
                      className="p-4 bg-gray-50 rounded-lg"
                      data-testid={`response-item-${response.id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-blue-600">
                          Gatilho: {response.trigger}
                        </span>
                        <div className="flex items-center space-x-2">
                          <Switch
                            checked={response.active}
                            onCheckedChange={(checked) =>
                              toggleAutoResponse(response.id, checked)
                            }
                            data-testid={`toggle-response-${response.id}`}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteAutoResponse(response.id)}
                            data-testid={`delete-response-${response.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700">
                        {response.response}
                      </p>
                    </div>
                  ))}
                  {autoResponses.length === 0 && (
                    <p className="text-center text-gray-500 py-8">
                      Nenhuma resposta automática configurada
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tutorials">
            <div className="flex flex-col bg-white p-6 gap-4 rounded-xl border shadow-sm">
              <div className="border-b pb-4">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  🧠 Cérebro do WILL (Tutoriais Autônomos)
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Cadastre problemas comuns e suas soluções (texto, vídeos ou
                  prints). A IA fará a leitura da dúvida do cliente e entregará
                  a resposta exata de forma automática.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-gray-50 border rounded-lg gap-4">
                <div className="flex-1">
                  <Label className="text-base font-bold text-gray-800">
                    Ativar Respostas Automáticas
                  </Label>
                  <p className="text-sm text-gray-500 mt-1">
                    Se ativado, o WILL tentará resolver o problema do cliente
                    sozinho usando a Base de Conhecimento abaixo antes de mandar
                    para a fila humana.
                  </p>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      className="w-6 h-6 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                      checked={config?.ai_auto_reply_enabled || false}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          ai_auto_reply_enabled: e.target.checked,
                        })
                      }
                    />
                  </div>
                  <Button
                    onClick={saveConfig}
                    className="bg-green-600 hover:bg-green-700 h-9"
                  >
                    Salvar Status
                  </Button>
                </div>
              </div>

              <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-100 mb-6 grid gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <Label>Nome do Problema / Tutorial</Label>
                  <Input
                    placeholder="Ex: Erro 429 na Emissão de NFe"
                    value={newTutorial.title}
                    onChange={(e) =>
                      setNewTutorial({ ...newTutorial, title: e.target.value })
                    }
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Palavras-Chave (Sintomas)</Label>
                  <Input
                    placeholder="Ex: nfe, 429, rejeição, falha sefaz, lote"
                    value={newTutorial.symptoms}
                    onChange={(e) =>
                      setNewTutorial({
                        ...newTutorial,
                        symptoms: e.target.value,
                      })
                    }
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Separe os sintomas e palavras-chave por vírgula para ajudar
                    a IA a encontrar mais fácil.
                  </p>
                </div>
                <div className="md:col-span-2">
                  <Label>
                    Texto da Solução (A resposta que a IA vai mandar)
                  </Label>
                  <Textarea
                    placeholder="Ex: Esse erro ocorre por falha de comunicação na Sefaz. Siga os passos do vídeo abaixo..."
                    rows={3}
                    value={newTutorial.solution_text}
                    onChange={(e) =>
                      setNewTutorial({
                        ...newTutorial,
                        solution_text: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>
                    Arquivo de Mídia (Vídeo, Imagem, PDF) ou Link Externo
                  </Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="Faça o upload do arquivo ou cole um link aqui..."
                      value={newTutorial.media_url}
                      onChange={(e) =>
                        setNewTutorial({
                          ...newTutorial,
                          media_url: e.target.value,
                        })
                      }
                      className="flex-1"
                    />

                    <input
                      type="file"
                      ref={mediaInputRef}
                      onChange={handleMediaUpload}
                      className="hidden"
                      accept="image/*,video/mp4,application/pdf"
                    />

                    <Button
                      variant="outline"
                      onClick={() => mediaInputRef.current?.click()}
                      disabled={isUploadingMedia}
                      className="shrink-0 flex items-center gap-2 bg-white hover:bg-gray-50 border-gray-300"
                    >
                      {isUploadingMedia ? (
                        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                      ) : (
                        <Upload className="w-4 h-4 text-gray-600" />
                      )}
                      Upload
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Arquivos enviados ficarão salvos diretamente no servidor da
                    empresa e serão entregues pelo WhatsApp.
                  </p>
                </div>
                <div className="md:col-span-2 flex justify-center sm:justify-end mt-2">
                  <Button
                    onClick={addTutorial}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Cadastrar Solução na IA
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                {tutorials.map((tut) => (
                  <div
                    key={tut.id}
                    className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-gray-50 border p-4 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1 pr-4">
                      <h3 className="font-bold text-gray-800">{tut.title}</h3>
                      <p className="text-xs text-gray-500 font-mono mt-1 mb-2 max-w-full truncate overflow-hidden">
                        Sintomas: {tut.symptoms}
                      </p>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {tut.solution_text}
                      </p>
                      {tut.media_url && (
                        <a
                          href={tut.media_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-blue-500 hover:underline mt-2 inline-block"
                        >
                          🔗 Mídia Anexada
                        </a>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteTutorial(tut.id)}
                      className="text-red-500 hover:bg-red-100 hover:text-red-700 mt-3 sm:mt-0"
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </div>
                ))}

                {tutorials.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <p>O Cérebro do WILL ainda está vazio.</p>
                    <p className="text-sm">
                      Cadastre o primeiro tutorial acima!
                    </p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="api-externa">
            <Card>
              <CardHeader>
                <CardTitle>Configuração de API Externa de Clientes</CardTitle>
                <CardDescription>
                  Configure uma API externa para buscar a lista de clientes
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col space-y-6 w-full">
                <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg w-full border border-blue-100">
                  <Switch
                    checked={config.use_external_api}
                    onCheckedChange={(checked) =>
                      setConfig({ ...config, use_external_api: checked })
                    }
                    data-testid="use-external-api-toggle"
                  />
                  <div>
                    <p className="font-bold text-blue-900">Usar API Externa</p>
                    <p className="text-sm text-blue-700/80">
                      Ative para buscar clientes de uma API externa ao invés do
                      sistema interno
                    </p>
                  </div>
                </div>

                {config.use_external_api && (
                  <div className="space-y-4 w-full animate-in fade-in slide-in-from-top-2 duration-300">
                    <div className="space-y-2 w-full">
                      <Label className="font-semibold text-gray-700">
                        URL da API Externa
                      </Label>
                      <Input
                        placeholder="https://api.example.com/clientes"
                        value={config.external_api_url || ""}
                        onChange={(e) =>
                          setConfig({
                            ...config,
                            external_api_url: e.target.value,
                          })
                        }
                        className="w-full bg-white"
                        data-testid="external-api-url-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        A API deve retornar um JSON com array de objetos
                        contendo: name, phone, email (opcional)
                      </p>
                    </div>

                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 w-full">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Exemplo de resposta esperada:
                      </p>
                      <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded-md overflow-x-auto custom-scrollbar w-full">
                        {`[
  {
    "name": "João Silva",
    "phone": "5511999999999",
    "email": "joao@example.com"
  },
  {
    "name": "Maria Santos",
    "phone": "5511888888888"
  }
]`}
                      </pre>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg w-full border border-green-100">
                  <Switch
                    checked={config.auto_menu_enabled}
                    onCheckedChange={(checked) =>
                      setConfig({ ...config, auto_menu_enabled: checked })
                    }
                    data-testid="auto-menu-toggle"
                  />
                  <div>
                    <p className="font-bold text-green-900">Menu Automático</p>
                    <p className="text-sm text-green-700/80">
                      Enviar automaticamente um menu de opções quando cliente
                      entrar em contato pela primeira vez
                    </p>
                  </div>
                </div>

                <div className="flex justify-end w-full pt-4">
                  <Button
                    onClick={saveConfig}
                    className="w-full sm:w-auto px-8 shadow-sm"
                    data-testid="save-api-config-btn"
                  >
                    <Save className="w-4 h-4 mr-2 shrink-0" />
                    Salvar Configurações
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="general">
            <Card>
              <CardHeader>
                <CardTitle>Configurações Gerais</CardTitle>
                <CardDescription>
                  Personalize o sistema com sua marca
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col space-y-6 w-full">
                <div className="space-y-4 w-full">
                  <div className="space-y-2 w-full">
                    <Label className="font-semibold text-gray-700">
                      Nome da Empresa
                    </Label>
                    <Input
                      placeholder="Nome da sua empresa"
                      value={config.company_name}
                      onChange={(e) =>
                        setConfig({ ...config, company_name: e.target.value })
                      }
                      className="w-full bg-white"
                      data-testid="company-name-input"
                    />
                  </div>

                  <div className="space-y-2 w-full">
                    <Label className="font-semibold text-gray-700">
                      Cor Primária
                    </Label>
                    <div className="flex gap-2 w-full">
                      <Input
                        type="color"
                        value={config.primary_color}
                        onChange={(e) =>
                          setConfig({
                            ...config,
                            primary_color: e.target.value,
                          })
                        }
                        className="w-16 sm:w-20 h-10 p-1 cursor-pointer bg-white shrink-0"
                        data-testid="primary-color-input"
                      />
                      <Input
                        type="text"
                        value={config.primary_color}
                        onChange={(e) =>
                          setConfig({
                            ...config,
                            primary_color: e.target.value,
                          })
                        }
                        placeholder="#3b82f6"
                        className="flex-1 bg-white uppercase font-mono"
                      />
                    </div>
                  </div>

                  <div className="space-y-2 w-full">
                    <Label className="font-semibold text-gray-700">
                      Logo da Empresa
                    </Label>
                    <div className="mt-2 w-full">
                      <label className="cursor-pointer block w-full">
                        <div className="flex items-center justify-center w-full h-32 bg-white border-2 border-dashed border-gray-300 rounded-lg hover:bg-gray-50 hover:border-blue-400 transition-colors">
                          <div className="text-center">
                            <Upload className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                            <p className="text-sm font-medium text-gray-700">
                              Clique para enviar logo
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              PNG, JPG ou SVG (Máx. 2MB)
                            </p>
                          </div>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={uploadLogo}
                          data-testid="logo-upload-input"
                        />
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end w-full pt-4">
                  <Button
                    onClick={saveConfig}
                    className="w-full sm:w-auto px-8 shadow-sm"
                    data-testid="save-config-btn"
                  >
                    <Save className="w-4 h-4 mr-2 shrink-0" />
                    Salvar Configurações
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default Config;
